import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { StaticSystemViews as PurchaseOrderStaticSystemViews } from "@redotech/redo-fulfillment-common/views/default-purchase-order-views";
import { StaticSystemViews as ShipmentStaticSystemViews } from "@redotech/redo-fulfillment-common/views/default-shipment-views";
import { StaticSystemViews as CatalogProductsStaticSystemViews } from "@redotech/redo-inventory-common/views/default-catalog-products-views";
import { StaticSystemViews as CycleCountStaticSystemViews } from "@redotech/redo-inventory-common/views/default-cycle-count-views";
import { StaticSystemViews as InventoryLevelsStaticSystemViews } from "@redotech/redo-inventory-common/views/default-inventory-levels-views";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import type { InventoryViewType } from "@redotech/redo-merchant-inventory-rpc/schema/views/inventory-view-type";
import {
  BaseFolderSchema,
  folderTypes,
} from "@redotech/redo-views-common/folders";
import {
  BaseViewWithId,
  CatalogProductsView,
  CycleCountView,
  InventoryLevelsView,
  PurchaseOrderView,
  ShipmentView,
} from "@redotech/redo-views-common/views";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as z from "zod";
import { CreateInventoryViewFolderModal } from "./common/create-inventory-view-folder-modal";
import { EditInventoryViewFolderModal } from "./common/edit-inventory-view-folder-modal";

type BaseFolder = z.infer<typeof BaseFolderSchema>;

// Shared interface for view operations
interface ViewsData<TView, TFolder> {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (updatedViews: TView[], updatedFolders: TFolder[]) => void;
  views?: TView[];
  folders?: TFolder[];
  reloadViews: () => void;
}

export type PurchaseOrderViewsData = ViewsData<
  PurchaseOrderView,
  folderTypes["purchase-orders"]
>;
export type ShipmentViewsData = ViewsData<
  ShipmentView,
  folderTypes["shipments"]
>;
export type InventoryLevelsViewsData = ViewsData<
  InventoryLevelsView,
  folderTypes["inventory-levels"]
>;
export type CatalogProductsViewsData = ViewsData<
  CatalogProductsView,
  folderTypes["catalog-products"]
>;
export type CycleCountsViewsData = ViewsData<
  CycleCountView,
  folderTypes["cycle-counts"]
>;

// Internal context to share the raw loaded data and mutations
interface InventoryViewsLoadData {
  viewsByType: {
    "purchase-orders": {
      views: PurchaseOrderView[];
      folders: folderTypes["purchase-orders"][];
      setViews: (views: PurchaseOrderView[]) => void;
      setFolders: (folders: folderTypes["purchase-orders"][]) => void;
    };
    shipments: {
      views: ShipmentView[];
      folders: folderTypes["shipments"][];
      setViews: (views: ShipmentView[]) => void;
      setFolders: (folders: folderTypes["shipments"][]) => void;
    };
    "inventory-levels": {
      views: InventoryLevelsView[];
      folders: folderTypes["inventory-levels"][];
      setViews: (views: InventoryLevelsView[]) => void;
      setFolders: (folders: folderTypes["inventory-levels"][]) => void;
    };
    "catalog-products": {
      views: CatalogProductsView[];
      folders: folderTypes["catalog-products"][];
      setViews: (views: CatalogProductsView[]) => void;
      setFolders: (folders: folderTypes["catalog-products"][]) => void;
    };
    "cycle-counts": {
      views: CycleCountView[];
      folders: folderTypes["cycle-counts"][];
      setViews: (views: CycleCountView[]) => void;
      setFolders: (folders: folderTypes["cycle-counts"][]) => void;
    };
  };
  reloadViews: () => void;
  mutations: {
    createInventoryViewFolder: (input: {
      viewType: InventoryViewType;
      name: string;
    }) => Promise<{ folderId: string }>;
    updateInventoryViewFolder: (input: {
      folderId: string;
      name: string;
    }) => Promise<object>;
    deleteInventoryViewFolder: (input: {
      viewType: InventoryViewType;
      folderId: string;
    }) => Promise<object>;
    updateInventoryViewOrdering: (input: {
      viewType: InventoryViewType;
      views: BaseViewWithId[];
      folders: BaseFolder[];
    }) => Promise<object>;
  };
}

const InventoryViewsLoadContext = createContext<
  InventoryViewsLoadData | undefined
>(undefined);

// Hook to get views data for a specific view type
export function useInventoryViews<
  T extends Exclude<InventoryViewType, "transfers">,
>(viewType: T) {
  const loadContext = useContext(InventoryViewsLoadContext);
  if (!loadContext) {
    throw new Error(
      "useInventoryViews must be used within InventoryViewsProvider",
    );
  }

  const { viewsByType, reloadViews, mutations } = loadContext;
  const { views, folders, setViews, setFolders } = viewsByType[viewType];

  const handleCreateFolder = useHandler(async (name: string) => {
    await mutations.createInventoryViewFolder({ viewType, name });
  });

  const handleEditFolder = useHandler(
    async (folderId: string, folderName: string) => {
      await mutations.updateInventoryViewFolder({ folderId, name: folderName });
    },
  );

  const handleDeleteFolder = useHandler(async (folderId: string) => {
    await mutations.deleteInventoryViewFolder({ viewType, folderId });
  });

  const updateOrdering = useHandler(
    async (updatedViews: BaseViewWithId[], updatedFolders: BaseFolder[]) => {
      setViews(updatedViews as typeof views);
      setFolders(updatedFolders as typeof folders);

      try {
        await mutations.updateInventoryViewOrdering({
          viewType,
          views: updatedViews,
          folders: updatedFolders,
        });
      } catch (error) {
        console.error("Error saving view and folder ordering", error);
      }
    },
  );

  return {
    views,
    folders,
    handleCreateFolder,
    handleEditFolder,
    handleDeleteFolder,
    updateOrdering,
    reloadViews,
  };
}

// Export contexts for consumers (same interface as before)
export const PurchaseOrderViewsContext = createContext<
  PurchaseOrderViewsData | undefined
>(undefined);

export const ShipmentViewsContext = createContext<
  ShipmentViewsData | undefined
>(undefined);

export const InventoryLevelsViewsContext = createContext<
  InventoryLevelsViewsData | undefined
>(undefined);

export const CatalogProductsViewsContext = createContext<
  CatalogProductsViewsData | undefined
>(undefined);

export const CycleCountsViewsContext = createContext<
  CycleCountsViewsData | undefined
>(undefined);

// Main provider that loads all data once
export const InventoryViewsProvider = memo(function InventoryViewsProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useRequiredContext(RedoInventoryRpcClientContext);

  const { mutations, ...viewsLoad } = useRpcData(
    client,
    "getInventoryViewsAndFolders",
    {
      input: {},
      mutations: [
        "createInventoryViewFolder",
        "updateInventoryViewFolder",
        "deleteInventoryViewFolder",
        "updateInventoryViewOrdering",
      ],
    },
  );

  // Separate state for optimistic updates
  const [purchaseOrderViews, setPurchaseOrderViews] = useState<
    PurchaseOrderView[]
  >([]);
  const [purchaseOrderFolders, setPurchaseOrderFolders] = useState<
    folderTypes["purchase-orders"][]
  >([]);
  const [shipmentViews, setShipmentViews] = useState<ShipmentView[]>([]);
  const [shipmentFolders, setShipmentFolders] = useState<
    folderTypes["shipments"][]
  >([]);
  const [inventoryLevelsViews, setInventoryLevelsViews] = useState<
    InventoryLevelsView[]
  >([]);
  const [inventoryLevelsFolders, setInventoryLevelsFolders] = useState<
    folderTypes["inventory-levels"][]
  >([]);
  const [catalogProductsViews, setCatalogProductsViews] = useState<
    CatalogProductsView[]
  >([]);
  const [catalogProductsFolders, setCatalogProductsFolders] = useState<
    folderTypes["catalog-products"][]
  >([]);
  const [cycleCountViews, setCycleCountViews] = useState<CycleCountView[]>([]);
  const [cycleCountFolders, setCycleCountFolders] = useState<
    folderTypes["cycle-counts"][]
  >([]);

  useEffect(() => {
    if (viewsLoad.value) {
      // Apply latest static definitions to system views while preserving
      // their DB-assigned position and folderId.
      const applyStaticDefinitions = <
        T extends {
          name: string;
          _id: string;
          position: number;
          folderId?: string | null;
        },
      >(
        dbViews: readonly T[],
        staticViews: Record<string, Omit<T, "_id">>,
      ): T[] => {
        const staticByName = new Map(
          Object.values(staticViews).map((v) => [v.name, v]),
        );
        return dbViews.map((dbView) => {
          const staticView = staticByName.get(dbView.name);
          if (!staticView) {
            return dbView;
          }
          return {
            ...staticView,
            _id: dbView._id,
            position: dbView.position,
            folderId: dbView.folderId,
          } as T;
        });
      };

      setPurchaseOrderViews(
        applyStaticDefinitions(
          viewsLoad.value["purchase-orders"].views,
          PurchaseOrderStaticSystemViews,
        ),
      );
      setPurchaseOrderFolders([...viewsLoad.value["purchase-orders"].folders]);

      setShipmentViews(
        applyStaticDefinitions(
          viewsLoad.value.shipments.views,
          ShipmentStaticSystemViews,
        ),
      );
      setShipmentFolders([...viewsLoad.value.shipments.folders]);

      setInventoryLevelsViews(
        applyStaticDefinitions(
          viewsLoad.value["inventory-levels"].views,
          InventoryLevelsStaticSystemViews,
        ),
      );
      setInventoryLevelsFolders([
        ...viewsLoad.value["inventory-levels"].folders,
      ]);

      setCatalogProductsViews(
        applyStaticDefinitions(
          viewsLoad.value["catalog-products"].views,
          CatalogProductsStaticSystemViews,
        ),
      );
      setCatalogProductsFolders([
        ...viewsLoad.value["catalog-products"].folders,
      ]);

      setCycleCountViews(
        applyStaticDefinitions(
          viewsLoad.value["cycle-counts"].views,
          CycleCountStaticSystemViews,
        ),
      );
      setCycleCountFolders([...viewsLoad.value["cycle-counts"].folders]);
    }
  }, [viewsLoad.value]);

  const reloadViews = useHandler(() => viewsLoad.refresh(true));

  const loadContextValue: InventoryViewsLoadData = {
    viewsByType: {
      "purchase-orders": {
        views: purchaseOrderViews,
        folders: purchaseOrderFolders,
        setViews: setPurchaseOrderViews,
        setFolders: setPurchaseOrderFolders,
      },
      shipments: {
        views: shipmentViews,
        folders: shipmentFolders,
        setViews: setShipmentViews,
        setFolders: setShipmentFolders,
      },
      "inventory-levels": {
        views: inventoryLevelsViews,
        folders: inventoryLevelsFolders,
        setViews: setInventoryLevelsViews,
        setFolders: setInventoryLevelsFolders,
      },
      "catalog-products": {
        views: catalogProductsViews,
        folders: catalogProductsFolders,
        setViews: setCatalogProductsViews,
        setFolders: setCatalogProductsFolders,
      },
      "cycle-counts": {
        views: cycleCountViews,
        folders: cycleCountFolders,
        setViews: setCycleCountViews,
        setFolders: setCycleCountFolders,
      },
    },
    reloadViews,
    mutations,
  };

  return (
    <InventoryViewsLoadContext.Provider value={loadContextValue}>
      <PurchaseOrderViewsSubProvider>
        <ShipmentViewsSubProvider>
          <InventoryLevelsViewsSubProvider>
            <CatalogProductsViewsSubProvider>
              <CycleCountsViewsSubProvider>
                {children}
              </CycleCountsViewsSubProvider>
            </CatalogProductsViewsSubProvider>
          </InventoryLevelsViewsSubProvider>
        </ShipmentViewsSubProvider>
      </PurchaseOrderViewsSubProvider>
    </InventoryViewsLoadContext.Provider>
  );
});

// Generic sub-provider factory for creating view type specific sub-providers
function createViewsSubProvider<
  TViewType extends Exclude<InventoryViewType, "transfers">,
  TView extends BaseViewWithId,
  TFolder extends BaseFolder,
>(
  viewType: TViewType,
  Context: React.Context<ViewsData<TView, TFolder> | undefined>,
) {
  return memo(function ViewsSubProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const loadContext = useContext(InventoryViewsLoadContext);
    if (!loadContext) {
      throw new Error(
        `ViewsSubProvider for ${viewType} must be used within InventoryViewsProvider`,
      );
    }

    const { viewsByType, reloadViews, mutations } = loadContext;
    const { views, folders, setViews, setFolders } = viewsByType[
      viewType
    ] as unknown as {
      views: TView[];
      folders: TFolder[];
      setViews: (views: TView[]) => void;
      setFolders: (folders: TFolder[]) => void;
    };

    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showEditFolderModal, setShowEditFolderModal] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState<TFolder | null>(null);

    const handleCreateFolder = useHandler(async (name: string) => {
      await mutations.createInventoryViewFolder({ viewType, name });
    });

    const handleEditFolder = useHandler(
      async (folderId: string, folderName: string) => {
        await mutations.updateInventoryViewFolder({
          folderId,
          name: folderName,
        });
      },
    );

    const handleDeleteFolder = useHandler(async (folderId: string) => {
      await mutations.deleteInventoryViewFolder({ viewType, folderId });
    });

    const updateOrdering = useHandler(
      async (updatedViews: TView[], updatedFolders: TFolder[]) => {
        setViews(updatedViews);
        setFolders(updatedFolders);

        try {
          await mutations.updateInventoryViewOrdering({
            viewType,
            views: updatedViews,
            folders: updatedFolders,
          });
        } catch (error) {
          console.error("Error saving view and folder ordering", error);
        }
      },
    );

    const openCreateFolderModal = useHandler(() => {
      setShowCreateFolderModal(true);
    });

    const openEditFolderModal = useHandler((folderId: string) => {
      const folder = folders.find((f) => f._id === folderId);
      if (folder) {
        setSelectedFolder(folder);
        setShowEditFolderModal(true);
      }
    });

    const reservedFolderNames = useMemo(() => {
      const names = new Set(folders.map((f) => f.name));
      if (selectedFolder) {
        names.delete(selectedFolder.name);
      }
      return names;
    }, [folders, selectedFolder]);

    const contextValue: ViewsData<TView, TFolder> = {
      openCreateFolderModal,
      openEditFolderModal,
      handleDeleteFolder,
      updateOrdering,
      views,
      folders,
      reloadViews,
    };

    return (
      <Context.Provider value={contextValue}>
        {children}
        <CreateInventoryViewFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
          reservedFolderNames={reservedFolderNames}
        />
        {selectedFolder && (
          <EditInventoryViewFolderModal
            deleteFolder={handleDeleteFolder}
            isOpen={showEditFolderModal}
            onClose={() => setShowEditFolderModal(false)}
            onSubmit={handleEditFolder}
            reservedFolderNames={reservedFolderNames}
            selectedFolder={selectedFolder}
          />
        )}
      </Context.Provider>
    );
  });
}

// Create sub-providers for each view type
const PurchaseOrderViewsSubProvider = createViewsSubProvider<
  "purchase-orders",
  PurchaseOrderView,
  folderTypes["purchase-orders"]
>("purchase-orders", PurchaseOrderViewsContext);

const ShipmentViewsSubProvider = createViewsSubProvider<
  "shipments",
  ShipmentView,
  folderTypes["shipments"]
>("shipments", ShipmentViewsContext);

const InventoryLevelsViewsSubProvider = createViewsSubProvider<
  "inventory-levels",
  InventoryLevelsView,
  folderTypes["inventory-levels"]
>("inventory-levels", InventoryLevelsViewsContext);

const CatalogProductsViewsSubProvider = createViewsSubProvider<
  "catalog-products",
  CatalogProductsView,
  folderTypes["catalog-products"]
>("catalog-products", CatalogProductsViewsContext);

const CycleCountsViewsSubProvider = createViewsSubProvider<
  "cycle-counts",
  CycleCountView,
  folderTypes["cycle-counts"]
>("cycle-counts", CycleCountsViewsContext);
